import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { useBackNavigation } from "@/hooks/useBackNavigation"

// Custom Dialog component that integrates back navigation
const Dialog = ({ open, onOpenChange, ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) => {
  useBackNavigation(open ?? false, () => {
    onOpenChange?.(false);
  }, 'dialog');
  
  return <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} {...props} />;
};

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm overflow-hidden",
      // Ensure closed overlay doesn't block clicks (critical for freeze prevention)
      "pointer-events-none sm:pointer-events-auto data-[state=closed]:pointer-events-none",
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full'
    hideCloseButton?: boolean
  }
>(({ className, children, size = 'md', hideCloseButton = false, ...props }, ref) => {
  const sizeClasses = {
    sm: 'md:max-w-sm',
    md: 'md:max-w-2xl',
    lg: 'md:max-w-4xl',
    xl: 'md:max-w-5xl',
    '2xl': 'md:max-w-6xl',
    full: 'sm:w-[100vw] sm:h-[100vh] sm:max-w-[100vw] sm:max-h-[100vh] sm:rounded-none sm:border-0'
  };

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className={cn(
          "fixed z-[100] flex flex-col gap-3 border shadow-xl duration-300 box-border",
          "[background:linear-gradient(90deg,hsl(var(--card))_0%,hsl(var(--card)/0.98)_100%)]",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          // Mobile: Fullscreen - padding moved to inner wrapper for scroll behavior
          "left-0 right-0 top-0 bottom-0 w-full max-w-none h-[100dvh] max-h-[100dvh] rounded-none",
          // Tablet/Desktop: Fixed height für konsistentes Layout
          "sm:inset-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%]",
          "sm:h-[85vh] sm:max-h-[85vh] sm:w-[80vw] sm:rounded-lg sm:border",
          "sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%]",
          "sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%]",
          "sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95",
          // Desktop: Größen-basiert
          sizeClasses[size],
          className
        )}
        {...props}
      >
        {/* Inner wrapper for proper flex layout with padding */}
        <div className="flex flex-col flex-1 min-h-0 max-h-full overflow-hidden p-4 sm:p-6">
          {children}
        </div>
        {!hideCloseButton && (
          <DialogPrimitive.Close className="absolute right-4 top-4 z-50 rounded-full opacity-90 ring-offset-background transition-opacity transition-transform duration-200 hover:opacity-100 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none bg-muted/80 hover:bg-muted p-2 shadow-sm touch-target-sm flex items-center justify-center">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left max-w-full pr-10 flex-shrink-0 mb-3",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2",
      "sticky bottom-0 bg-background border-t pt-4 pb-safe mt-4 sm:static sm:border-0 sm:pt-0 sm:pb-0 sm:mt-0",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight text-foreground break-words",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}